/**
 * @graphql cc
 */
import React, { useCallback, Fragment } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import type { QueryResult } from 'react-apollo';

import { DatabasePrerequisites } from '@confluence/content-prerequisites/entry-points/DatabasePrerequisites';
import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { DATABASE_VIEW } from '@confluence/named-routes/entry-points/routes';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent';
import { CommentProvider } from '@confluence/content-types-bridge-comments';
import { expVal } from '@confluence/feature-experiments';
import { useContentRestrictionSubscriptionEvent } from '@confluence/editor-features/entry-points/useContentRestrictionSubscriptionEvent';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { fg } from '@confluence/feature-gating';

import { DatabaseShell } from './DatabaseShell';
import { DatabaseUnifiedQuery } from './graphql/DatabaseUnifiedQuery.graphql';
import type {
	DatabaseUnifiedQuery as DatabaseUnifiedQueryType,
	DatabaseUnifiedQueryVariables,
} from './graphql/__types__/DatabaseUnifiedQuery';

type DatabaseContainerType = {
	contentId: string;
	spaceKey: string;
};

export type DatabaseUnifiedQueryResultType = Pick<
	QueryResult<DatabaseUnifiedQueryType>,
	'data' | 'error' | 'loading'
>;

const DatabaseContainerComponent = ({ contentId, spaceKey }: DatabaseContainerType) => {
	const { networkStatus, refetch, ...databaseUnifiedQueryResult } = useQuery<
		DatabaseUnifiedQueryType,
		DatabaseUnifiedQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		DatabaseUnifiedQuery,
		{
			variables: {
				contentId,
				spaceKey,
				// The DSP query doesn't currently user the content version value, so using a fixed value
				contentVersion: 1,
			},
			notifyOnNetworkStatusChange: true,
			context: { single: true },
		},
	);

	const { data, loading } = databaseUnifiedQueryResult;

	const content = data?.content?.nodes || [];
	const contentReferenceId = content[0]?.referenceId;
	const spaceId = content[0]?.space?.id;
	const spaceName = content[0]?.space?.name;
	const title = content[0]?.title || '';
	const identity = {
		accountId: data?.user?.id,
		nickname: data?.user?.displayName,
		picture: data?.user?.photos?.find((photo) => photo?.isPrimary)?.value,
		email: data?.user?.emails?.find((email) => email?.isPrimary)?.value,
		locale: data?.user?.locale,
	};

	const onEventHandler = useCallback(() => {
		void refetch();
	}, [refetch]);

	const onRestrictionUpdatedPubSubEvent = useCallback(() => {
		if (
			!['DUAL', 'GRAPHQL'].includes(
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
			) ||
			!expVal('cc_fps_migration_content-restriction-updated', 'isEnabled', false)
		) {
			onEventHandler();
		}
	}, [onEventHandler]);

	usePubSubEvent({
		contentId,
		contentType: 'database',
		eventName: `restrictionUpdated:database`,
		attribution: Attribution.CONTENT_TYPES,
		onEvent: onRestrictionUpdatedPubSubEvent,
		skip:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expVal('cc_fps_migration_content-restriction-updated', 'isEnabled', false) &&
			fg('confluence_enable_subscriptions-context-provider') &&
			StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
				'GRAPHQL',
	});

	useContentRestrictionSubscriptionEvent({
		contentId,
		contentType: 'database',
		callback: onEventHandler,
		subscriberId: 'DatabaseContainerSubscription',
	});

	const pageExport = data?.dataSecurityPolicy?.isActionEnabledForContent?.allowed;
	const dataSecurityPolicyPageExport = pageExport != undefined ? pageExport : null;

	return (
		<Fragment>
			<DatabasePrerequisites route={DATABASE_VIEW} contentQueryResult={databaseUnifiedQueryResult}>
				<CommentProvider>
					{expVal('confluence_modernized_move_dialog', 'isEnabled', false) && (
						<MovePageSuccessFlag />
					)}
					<DatabaseShell
						spaceKey={spaceKey}
						contentId={contentId}
						contentReferenceId={contentReferenceId}
						loading={loading}
						spaceId={spaceId}
						queryNetworkStatus={networkStatus}
						spaceName={spaceName}
						title={title}
						identity={identity}
						refetch={refetch}
						dataSecurityPolicyPageExport={dataSecurityPolicyPageExport}
					/>
				</CommentProvider>
			</DatabasePrerequisites>
		</Fragment>
	);
};

export const DatabaseContainer = DatabaseContainerComponent;
